import type { ModelCatalogEntry } from '../types/sessions';

export interface ResolveMessageFieldContextWindowOptions {
  selectedModel: string | null | undefined;
  models?: ModelCatalogEntry[] | null;
  activeSessionModel?: string | null;
  activeSessionContextTokens?: number | null;
  defaultModel?: string | null;
  defaultContextTokens?: number | null;
}

function normalizeModelId(value: string | null | undefined): string | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

function normalizePositiveInt(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : null;
}

function modelIdsMatch(a: string, b: string): boolean {
  return a === b || a.endsWith(`/${b}`) || b.endsWith(`/${a}`);
}

function buildModelCandidates(model: ModelCatalogEntry): string[] {
  const id = normalizeModelId(model.id);
  if (!id) return [];

  const candidates = new Set<string>([id]);
  const provider = normalizeModelId(model.provider);
  if (provider && !id.includes('/')) {
    candidates.add(`${provider}/${id}`);
  }

  return Array.from(candidates);
}

export function getContextWindowForModel(
  modelId: string | null | undefined,
  models?: ModelCatalogEntry[] | null,
): number | null {
  const normalizedModelId = normalizeModelId(modelId);
  if (!normalizedModelId || !models?.length) return null;

  for (const model of models) {
    const contextWindow = normalizePositiveInt(model.contextWindow);
    if (contextWindow === null) continue;

    if (buildModelCandidates(model).some((candidate) => modelIdsMatch(candidate, normalizedModelId))) {
      return contextWindow;
    }
  }

  return null;
}

export function resolveMessageFieldContextWindow(
  options: ResolveMessageFieldContextWindowOptions,
): number | null {
  const selectedModel = normalizeModelId(options.selectedModel);

  if (selectedModel && selectedModel !== 'default') {
    return getContextWindowForModel(selectedModel, options.models);
  }

  return (
    getContextWindowForModel(options.activeSessionModel, options.models)
    ?? normalizePositiveInt(options.activeSessionContextTokens)
    ?? getContextWindowForModel(options.defaultModel, options.models)
    ?? normalizePositiveInt(options.defaultContextTokens)
    ?? null
  );
}
